/**
 * src/shell/ui/App.tsx — 屏切换与全局浮层（设计稿 §1.4 导航结构）。
 *
 * - 屏状态机：S0（仅首启）→ S1 驾驶舱 → 各深屏（≤2 层，统一回到桌前）；
 * - 启动恢复：进桌面前 resumeOnBoot（幂等，引擎 §6）；
 * - 结算简报：监听快照，save.projects_completed 递增帧 → 全屏浮层（§2 Phase 6，M5B-B6 锚点）。
 */

import { useEffect, useRef, useState } from 'react'
import { COPY } from '../copy'
import { useSave } from '../gm-client/hooks'
import { resumeOnBoot } from '../gm-client/master'
import type { SaveBundle } from '../../gm'
import { SettlementOverlay, type SettlementInfo } from './components/SettlementOverlay'

// ── 结算浮层触发检测（M5B-B6 修复）─────────────────────────────────────────
//
// 触发锚 = save.projects_completed 递增帧（settle 写序 ③）。病灶：旧逻辑以
// 「项目 status→completed」（写序 ②）为触发帧，但 funds/level/bailout 在 ③ 才落盘，
// 浮层永远在入账前一帧弹出，bailedOut/leveledTo 恒为旧值（低保/升级 100% 丢失）。
// ③ 帧 save 单帧原子携带全部账务，且 ① 评审落盘、② 项目封盘已在场——弹出首帧即正确。
// settle 写序是防双倍结算的崩溃安全设计，外壳只换锚点，settle 一字不动。

export interface SettlementFrame {
  /** save.projects_completed——触发锚（③ 帧才递增） */
  projectsCompleted: number
  /** 已播报过的 completed 项目（配对基线；② 帧封盘不等于已播报——病灶修复的关键） */
  accountedIds: Set<string>
  level: number
  bailoutTimes: number
}

/** 纯函数便于回归测试。prev=null → 只建基线（镇店存档首帧不弹简报）。 */
export function detectSettlement(
  prev: SettlementFrame | null,
  bundle: SaveBundle,
): { frame: SettlementFrame; info: SettlementInfo | null } {
  const completedIds = new Set(
    bundle.projects.projects.filter((p) => p.status === 'completed').map((p) => p.id),
  )
  if (!prev) {
    // 首帧基线：既有 completed（镇店存档）全部记为已播报
    return {
      frame: {
        projectsCompleted: bundle.save.projects_completed,
        accountedIds: completedIds,
        level: bundle.save.level,
        bailoutTimes: bundle.save.bailout.times_used,
      },
      info: null,
    }
  }
  const frame: SettlementFrame = {
    projectsCompleted: bundle.save.projects_completed,
    accountedIds: prev.accountedIds,
    level: bundle.save.level,
    bailoutTimes: bundle.save.bailout.times_used,
  }
  if (frame.projectsCompleted <= prev.projectsCompleted) return { frame, info: null }
  // 新 completed 项目与评审配对：同帧 ①② 已就绪，直接可查（保持旧语义，取第一个）
  for (const id of completedIds) {
    if (!prev.accountedIds.has(id)) {
      frame.accountedIds = new Set([...prev.accountedIds, id]) // 播报与否都只认一次
      const review = bundle.reviews.reviews.find((r) => r.project_id === id)
      if (!review) return { frame, info: null }
      return {
        frame,
        info: {
          projectId: id,
          review,
          leveledTo: frame.level > prev.level ? frame.level : null,
          bailedOut: frame.bailoutTimes > prev.bailoutTimes,
          bailoutTimes: frame.bailoutTimes,
        },
      }
    }
  }
  return { frame, info: null }
}
import { Toasts } from './components/Toasts'
import { DESK, type Screen } from './nav'
import { CockpitScreen } from './screens/CockpitScreen'
import { ComputerScreen } from './screens/ComputerScreen'
import { DecisionScreen } from './screens/DecisionScreen'
import { NewProjectScreen } from './screens/NewProjectScreen'
import { PhoneScreen } from './screens/PhoneScreen'
import { ReviewScreen } from './screens/ReviewScreen'
import { ShelfScreen } from './screens/ShelfScreen'
import { TitleScreen } from './screens/TitleScreen'

export function App({ firstRun }: { firstRun: boolean }) {
  const [screen, setScreen] = useState<Screen>(firstRun ? { name: 'title' } : DESK)
  const [settlement, setSettlement] = useState<SettlementInfo | null>(null)
  const snapshot = useSave()

  // 启动恢复（幂等；首启走完 S0 进桌面时同样安全——内部一次性旗标）
  useEffect(() => {
    void resumeOnBoot()
  }, [])

  // 结算检测：锚 save.projects_completed 递增帧（见 detectSettlement 头注，M5B-B6）
  const prevRef = useRef<SettlementFrame | null>(null)
  useEffect(() => {
    const { frame, info } = detectSettlement(prevRef.current, snapshot.bundle)
    prevRef.current = frame
    if (info) setSettlement(info)
  }, [snapshot])

  const go = (s: Screen) => setScreen(s)

  return (
    <>
      {screen.name === 'title' && <TitleScreen onEnter={() => go(DESK)} />}
      {screen.name === 'cockpit' && <CockpitScreen go={go} />}
      {screen.name === 'computer' && (
        <ComputerScreen tab={screen.tab} projectId={screen.projectId} go={go} />
      )}
      {screen.name === 'decisions' && <DecisionScreen decisionId={screen.decisionId} go={go} />}
      {screen.name === 'phone' && <PhoneScreen dept={screen.dept} go={go} />}
      {screen.name === 'shelf' && <ShelfScreen projectId={screen.projectId} go={go} />}
      {screen.name === 'newProject' && <NewProjectScreen go={go} />}
      {screen.name === 'review' && <ReviewScreen projectId={screen.projectId} go={go} />}

      {settlement && (
        <SettlementOverlay
          info={settlement}
          onViewReport={(pid) => {
            setSettlement(null)
            go({ name: 'review', projectId: pid })
          }}
          onClose={() => {
            setSettlement(null)
            go(DESK)
          }}
        />
      )}

      <Toasts />
      <div className="graybox-banner">{COPY.graybox}</div>
    </>
  )
}
